// src/features/auth/authSlice.ts
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { login, fetchCurrentUser, fetchCurrentUserConversations, fetchCurrentUserNotifications, markAllNotificationsAsRead } from "./authThunks";
import { clearAuthClientSession, hasAuthClientSession, markAuthClientSession } from "./tokenCookie";

interface AuthState {
  user: any | null;
  token: string | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  bookingStreamTick: number;
  notificationCount: number;
  notificationTick: number;
  notifications: BookingValidatedNotificationItem[];
  chatUnreadCount: number;
  chatTick: number;
  chatThreads: ChatThreadItem[];
  chatReadByInterlocutor: Record<string, string>;
  chatTypingByInterlocutor: Record<string, number>;
  latestChatMessage: {
    id: string;
    interlocutorId: string;
    content: string;
    createdAt: string;
    isMine: boolean;
    readAt?: string;
  } | null;
  latestDeletedChatMessage: {
    interlocutorId: string;
    messageId: string;
    at: string;
  } | null;
}

type BookingBookedDeltaPayload = {
  type?: string;
  bookingId?: string;
  date?: string;
  booked?: boolean;
};

type BookingDatesUpdatedPayload = {
  type?: string;
  bookingId?: string;
  reason?: string;
  bookingDates?: unknown[];
};

type FavoriteTogglePayload = {
  adId: string;
  favorite: boolean;
};

type BookingValidatedNotificationPayload = {
  count?: number;
  notification?: Partial<BookingValidatedNotificationItem>;
};

export type BookingValidatedNotificationItem = {
  id: string;
  type?: string;
  ownerName?: string;
  adTitle?: string;
  adsId?: string;
  validatedDate?: string;
  reservationDates?: string[];
  reportId?: string;
  reportMessage?: string;
  reporterName?: string;
  reporterEmail?: string;
  chatTargetUserId?: string;
  createdAt: string;
  read?: boolean;
};

export type ChatThreadItem = {
  interlocutorId: string;
  interlocutorName: string;
  interlocutorEmail?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  lastReadAt?: string;
};

type IncomingChatPayload = {
  message?: {
    interlocutorId?: string;
    isMine?: boolean;
    content?: string;
    createdAt?: string;
  };
  interlocutor?: {
    id?: string;
    displayName?: string;
    email?: string;
  };
};

type ChatReadPayload = {
  interlocutorId?: string;
  readAt?: string;
};

type ChatTypingPayload = {
  interlocutorId?: string;
  isTyping?: boolean;
  at?: string;
};

type ChatMessageDeletedPayload = {
  interlocutorId?: string;
  messageId?: string;
  at?: string;
};

const normalizeChatThreadItem = (input: any): ChatThreadItem | null => {
  const interlocutorId = String(input?.interlocutorId || input?.interlocutor?.id || "").trim();
  if (!interlocutorId) return null;
  const interlocutorName = String(input?.interlocutor?.displayName || "").trim() || "Utilisateur";
  return {
    interlocutorId,
    interlocutorName,
    interlocutorEmail: typeof input?.interlocutor?.email === "string" ? input.interlocutor.email : undefined,
    lastMessage: typeof input?.lastMessage === "string" ? input.lastMessage : "",
    lastMessageAt: typeof input?.lastMessageAt === "string" && input.lastMessageAt.trim()
      ? input.lastMessageAt
      : new Date().toISOString(),
    unreadCount: Number.isFinite(Number(input?.unreadCount)) ? Math.max(0, Math.floor(Number(input?.unreadCount))) : 0,
    lastReadAt: typeof input?.lastReadAt === "string" && input.lastReadAt.trim() ? input.lastReadAt : undefined,
  };
};

const normalizeNotificationItem = (
  input: Partial<BookingValidatedNotificationItem> | undefined
): BookingValidatedNotificationItem | null => {
  if (!input || typeof input !== "object") return null;
  const id = typeof input.id === "string" && input.id.trim()
    ? input.id.trim()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return {
    id,
    type: typeof input.type === "string" ? input.type.trim() : undefined,
    ownerName: typeof input.ownerName === "string" ? input.ownerName.trim() : undefined,
    adTitle: typeof input.adTitle === "string" ? input.adTitle.trim() : undefined,
    adsId: typeof (input as any).adsId === "string" ? String((input as any).adsId).trim() : undefined,
    validatedDate: typeof input.validatedDate === "string" ? input.validatedDate.trim() : undefined,
    reservationDates: Array.isArray((input as any).reservationDates)
      ? Array.from(new Set((input as any).reservationDates
          .map((value: unknown) => (typeof value === "string" ? value.trim() : ""))
          .filter((value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value))))
      : undefined,
    reportId: typeof (input as any).reportId === "string" ? String((input as any).reportId).trim() : undefined,
    reportMessage: typeof (input as any).reportMessage === "string" ? String((input as any).reportMessage).trim() : undefined,
    reporterName: typeof (input as any).reporterName === "string" ? String((input as any).reporterName).trim() : undefined,
    reporterEmail: typeof (input as any).reporterEmail === "string" ? String((input as any).reporterEmail).trim() : undefined,
    chatTargetUserId: typeof (input as any).chatTargetUserId === "string" ? String((input as any).chatTargetUserId).trim() : undefined,
    createdAt: typeof input.createdAt === "string" && input.createdAt.trim()
      ? input.createdAt.trim()
      : new Date().toISOString(),
    read: typeof input.read === "boolean" ? input.read : false,
  };
};

const toDateKey = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const prefixedDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (prefixedDate) return prefixedDate[1];
    return null;
  }
  if (typeof value === "object") {
    const dateLike =
      (value as any)?.date ??
      (value as any)?.bookingDate ??
      (value as any)?.day ??
      null;
    if (!dateLike) return null;
    return toDateKey(dateLike);
  }
  return null;
};

const toBookedBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object" && "booked" in (value as Record<string, unknown>)) {
    return Boolean((value as { booked?: unknown }).booked);
  }
  return false;
};

const countUnreadThreads = (threads: ChatThreadItem[]): number =>
  threads.reduce((acc, item) => acc + (Number(item?.unreadCount || 0) > 0 ? 1 : 0), 0);

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  initialized: !hasAuthClientSession(),
  error: null,
  bookingStreamTick: 0,
  notificationCount: 0,
  notificationTick: 0,
  notifications: [],
  chatUnreadCount: 0,
  chatTick: 0,
  chatThreads: [],
  chatReadByInterlocutor: {},
  chatTypingByInterlocutor: {},
  latestChatMessage: null,
  latestDeletedChatMessage: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.initialized = true;
      state.bookingStreamTick = 0;
      state.notificationCount = 0;
      state.notificationTick = 0;
      state.notifications = [];
      state.chatUnreadCount = 0;
      state.chatTick = 0;
      state.chatThreads = [];
      state.chatReadByInterlocutor = {};
      state.chatTypingByInterlocutor = {};
      state.latestChatMessage = null;
      state.latestDeletedChatMessage = null;
      clearAuthClientSession();
    },
    applyBookingBookedDelta: (state, action: { payload: BookingBookedDeltaPayload }) => {
      state.bookingStreamTick += 1;
      if (!state.user) return;
      const targetDate = toDateKey(action.payload?.date);
      if (!targetDate || typeof action.payload?.booked !== "boolean") return;

      if (!Array.isArray(state.user.bookingDates)) {
        state.user.bookingDates = [];
      }

      let hasUpdatedExistingDate = false;

      state.user.bookingDates = state.user.bookingDates.map((entry: unknown) => {
        const entryDate = toDateKey(entry);
        if (entryDate !== targetDate) return entry;

        hasUpdatedExistingDate = true;
        if (entry && typeof entry === "object") {
          return {
            ...(entry as Record<string, unknown>),
            date: targetDate,
            booked: action.payload.booked,
          };
        }

        return { date: targetDate, booked: action.payload.booked };
      });

      if (!hasUpdatedExistingDate) {
        state.user.bookingDates.push({
          date: targetDate,
          booked: action.payload.booked,
        });
      }
    },
    applyBookingDatesUpdated: (state, action: { payload: BookingDatesUpdatedPayload }) => {
      state.bookingStreamTick += 1;
      if (!state.user) return;

      const incoming = Array.isArray(action.payload?.bookingDates) ? action.payload.bookingDates : [];
      const mergedByDate = new Map<string, { date: string; booked: boolean }>();

      incoming.forEach((entry: unknown) => {
        const dateKey = toDateKey(entry);
        if (!dateKey) return;
        const nextBooked = toBookedBoolean(entry);
        mergedByDate.set(dateKey, {
          date: dateKey,
          // Keep backend truth value; do not OR with previous state,
          // otherwise a previously true date can never go back to false.
          booked: nextBooked,
        });
      });

      state.user.bookingDates = Array.from(mergedByDate.values()).sort((a, b) => a.date.localeCompare(b.date));
    },
    setFavoriteAdState: (state, action: { payload: FavoriteTogglePayload }) => {
      if (!state.user) return;
      const adId = String(action.payload?.adId || "").trim();
      if (!adId) return;

      const current = Array.isArray(state.user.favoriteAds)
        ? state.user.favoriteAds.map((item: unknown) => String(item))
        : [];
      const next = new Set(current);

      if (action.payload.favorite) next.add(adId);
      else next.delete(adId);

      state.user.favoriteAds = Array.from(next);
    },
    addBookingValidatedNotification: (state, action: PayloadAction<BookingValidatedNotificationPayload | undefined>) => {
      const hasNotification = Boolean(action.payload?.notification);
      const increment = Number(action.payload?.count || (hasNotification ? 1 : 1));
      const safeIncrement = Number.isFinite(increment) && increment > 0 ? Math.floor(increment) : 1;
      state.notificationTick += 1;

      if (hasNotification) {
        const normalized = normalizeNotificationItem(action.payload?.notification);
        if (normalized) {
          const existingIndex = state.notifications.findIndex((item) => item.id === normalized.id);
          if (existingIndex === -1) {
            state.notifications.unshift(normalized);
            state.notificationCount += normalized.read ? 0 : 1;
          } else {
            const previous = state.notifications[existingIndex];
            state.notifications[existingIndex] = normalized;
            if (previous?.read && !normalized.read) state.notificationCount += 1;
            if (!previous?.read && normalized.read && state.notificationCount > 0) state.notificationCount -= 1;
          }
          state.notifications.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        }

        if (state.notifications.length > 120) {
          state.notifications.length = 120;
        }
      } else {
        state.notificationCount += safeIncrement;
      }
    },
    clearBookingNotifications: (state) => {
      state.notificationCount = 0;
      state.notifications = state.notifications.map((item) => ({ ...item, read: true }));
    },
    addIncomingChatMessage: (state, action: PayloadAction<IncomingChatPayload | undefined>) => {
      const payload = action.payload || {};
      const message = payload.message || {};
      const interlocutorFromPayload = payload.interlocutor || {};
      const interlocutorId = String(message?.interlocutorId || interlocutorFromPayload?.id || "").trim();
      if (!interlocutorId) return;

      const lastMessage = typeof message?.content === "string" ? message.content : "";
      const lastMessageAt = typeof message?.createdAt === "string" && message.createdAt.trim()
        ? message.createdAt
        : new Date().toISOString();
      const isMine = Boolean(message?.isMine);

      const existingIndex = state.chatThreads.findIndex((item) => item.interlocutorId === interlocutorId);
      if (existingIndex === -1) {
        state.chatThreads.unshift({
          interlocutorId,
          interlocutorName: String(interlocutorFromPayload?.displayName || "").trim() || "Utilisateur",
          interlocutorEmail: typeof interlocutorFromPayload?.email === "string" ? interlocutorFromPayload.email : undefined,
          lastMessage,
          lastMessageAt,
          unreadCount: isMine ? 0 : 1,
        });
      } else {
        const existing = state.chatThreads[existingIndex];
        const nextUnread = isMine ? existing.unreadCount : existing.unreadCount + 1;
        state.chatThreads[existingIndex] = {
          ...existing,
          interlocutorName: String(interlocutorFromPayload?.displayName || existing.interlocutorName || "").trim() || "Utilisateur",
          interlocutorEmail: typeof interlocutorFromPayload?.email === "string"
            ? interlocutorFromPayload.email
            : existing.interlocutorEmail,
          lastMessage,
          lastMessageAt,
          unreadCount: nextUnread,
        };
      }

      state.chatThreads.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
      state.chatUnreadCount = countUnreadThreads(state.chatThreads);
      delete state.chatTypingByInterlocutor[interlocutorId];
      state.latestChatMessage = {
        id: typeof (message as any)?.id === "string" && String((message as any).id).trim()
          ? String((message as any).id).trim()
          : `${interlocutorId}-${lastMessageAt}-${isMine ? "mine" : "theirs"}`,
        interlocutorId,
        content: lastMessage,
        createdAt: lastMessageAt,
        isMine,
        readAt: typeof (message as any)?.readAt === "string" && String((message as any).readAt).trim()
          ? String((message as any).readAt).trim()
          : undefined,
      };
      state.chatTick += 1;
    },
    markChatThreadAsReadLocal: (state, action: PayloadAction<{ interlocutorId: string } | undefined>) => {
      const interlocutorId = String(action.payload?.interlocutorId || "").trim();
      if (!interlocutorId) return;
      const index = state.chatThreads.findIndex((item) => item.interlocutorId === interlocutorId);
      if (index === -1) return;
      state.chatThreads[index].unreadCount = 0;
      state.chatUnreadCount = countUnreadThreads(state.chatThreads);
    },
    applyChatReadReceipt: (state, action: PayloadAction<ChatReadPayload | undefined>) => {
      const interlocutorId = String(action.payload?.interlocutorId || "").trim();
      if (!interlocutorId) return;
      const readAt = typeof action.payload?.readAt === "string" && action.payload.readAt.trim()
        ? action.payload.readAt
        : new Date().toISOString();
      state.chatReadByInterlocutor[interlocutorId] = readAt;
      const index = state.chatThreads.findIndex((item) => item.interlocutorId === interlocutorId);
      if (index !== -1) {
        state.chatThreads[index].lastReadAt = readAt;
      }
    },
    applyChatTypingSignal: (state, action: PayloadAction<ChatTypingPayload | undefined>) => {
      const interlocutorId = String(action.payload?.interlocutorId || "").trim();
      if (!interlocutorId) return;
      const isTyping = Boolean(action.payload?.isTyping);
      if (isTyping) {
        state.chatTypingByInterlocutor[interlocutorId] = Date.now() + 4500;
      } else {
        delete state.chatTypingByInterlocutor[interlocutorId];
      }
    },
    applyChatMessageDeleted: (state, action: PayloadAction<ChatMessageDeletedPayload | undefined>) => {
      const interlocutorId = String(action.payload?.interlocutorId || "").trim();
      const messageId = String(action.payload?.messageId || "").trim();
      if (!interlocutorId || !messageId) return;
      state.latestDeletedChatMessage = {
        interlocutorId,
        messageId,
        at: typeof action.payload?.at === "string" && action.payload.at.trim()
          ? action.payload.at
          : new Date().toISOString(),
      };
      delete state.chatTypingByInterlocutor[interlocutorId];
      state.chatTick += 1;
    },
    touchBookingStreamTick: (state) => {
      state.bookingStreamTick += 1;
    },
  },
  extraReducers: (builder) => {
    // LOGIN
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.user = action.payload;
        state.token = null;
        state.bookingStreamTick = 0;
        state.notifications = [];
        state.notificationCount = 0;
        state.chatUnreadCount = 0;
        state.chatTick = 0;
        state.chatThreads = [];
        state.chatReadByInterlocutor = {};
        state.chatTypingByInterlocutor = {};
        state.latestChatMessage = null;
        state.latestDeletedChatMessage = null;
        markAuthClientSession();
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        const payload = action.payload as any;
        state.error = typeof payload === "string"
          ? payload
          : (payload?.message?.fr || payload?.message?.en || payload?.message?.ar || null);
      });

    // FETCH CURRENT USER
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        if (action.payload) {
          state.user = action.payload;
          markAuthClientSession();
        } else {
          state.user = null;
          clearAuthClientSession();
        }
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
      });

    builder
      .addCase(fetchCurrentUserNotifications.fulfilled, (state, action) => {
        const incoming = Array.isArray(action.payload?.data) ? action.payload.data : [];
        const normalized = incoming
          .map((item) => normalizeNotificationItem(item))
          .filter((item): item is BookingValidatedNotificationItem => Boolean(item))
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        state.notifications = normalized.slice(0, 120);
        const unreadFromPayload = Number(action.payload?.unreadCount || 0);
        if (Number.isFinite(unreadFromPayload) && unreadFromPayload >= 0) {
          state.notificationCount = Math.floor(unreadFromPayload);
        } else {
          state.notificationCount = state.notifications.filter((item) => !item.read).length;
        }
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notificationCount = 0;
        state.notifications = state.notifications.map((item) => ({ ...item, read: true }));
      })
      .addCase(fetchCurrentUserConversations.fulfilled, (state, action) => {
        const incoming = Array.isArray(action.payload?.data) ? action.payload.data : [];
        const normalized = incoming
          .map((item) => normalizeChatThreadItem(item))
          .filter((item): item is ChatThreadItem => Boolean(item))
          .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
        state.chatThreads = normalized.slice(0, 200);
        state.chatThreads = state.chatThreads.map((thread) => ({
          ...thread,
          lastReadAt: state.chatReadByInterlocutor[thread.interlocutorId] || thread.lastReadAt,
        }));
        state.chatUnreadCount = countUnreadThreads(state.chatThreads);
      });
  },
});

export const {
  logout,
  applyBookingBookedDelta,
  applyBookingDatesUpdated,
  touchBookingStreamTick,
  setFavoriteAdState,
  addBookingValidatedNotification,
  clearBookingNotifications,
  addIncomingChatMessage,
  markChatThreadAsReadLocal,
  applyChatReadReceipt,
  applyChatTypingSignal,
  applyChatMessageDeleted,
} = authSlice.actions;
export default authSlice.reducer;
